import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Map, BarChart3, PieChart, TrendingUp, Star, Sparkles, ArrowRight } from 'lucide-react';
import Header from '@/components/Header';
import Headline from '@/components/Headline';
import SectionDivider from '@/components/SectionDivider';
import Overview from '@/components/Overview';
import KeyFeatures from '@/components/KeyFeatures';
import WaitlistSection from '@/components/WaitlistSection';
import HowItWorks from '@/components/HowItWorks';
import Footer from '@/components/Footer';
import { submitEmailToSupabase, type EmailSubmissionFormSource } from '@/lib/supabase';
import { trackEmailSubmissionSuccess, trackEmailSubmissionError } from '@/lib/analytics';

const Index = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent, formSource: EmailSubmissionFormSource) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    
    try {
      const result = await submitEmailToSupabase(email, formSource);
      
      if (result.success) {
        // Track successful submission
        trackEmailSubmissionSuccess(formSource, email);
        
        toast({
          title: "Welcome to the team!",
          description: "You've been added to our waiting list. Carto will be in touch soon!",
        });
        setEmail('');
      } else {
        // Track failed submission
        trackEmailSubmissionError(formSource, email, result.error || 'Unknown error');
        
        toast({
          title: "Oops! Something went wrong",
          description: result.error || "Please try again later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Track failed submission
      trackEmailSubmissionError(formSource, email, error instanceof Error ? error.message : 'Network error');
      
      toast({
        title: "Oops! Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 text-blue-100 opacity-20 animate-pulse">
          <Map size={120} />
        </div>
        <div className="absolute top-40 right-20 text-indigo-200 opacity-15 animate-bounce">
          <BarChart3 size={80} />
        </div>
        <div className="absolute bottom-40 left-20 text-slate-200 opacity-20">
          <PieChart size={100} />
        </div>
        <div className="absolute top-1/2 right-10 text-blue-100 opacity-10">
          <TrendingUp size={150} />
        </div>
        <div className="absolute top-32 left-1/3 text-yellow-200 opacity-30 animate-pulse">
          <Star size={24} />
        </div>
        <div className="absolute bottom-60 right-1/4 text-blue-200 opacity-25 animate-bounce">
          <Sparkles size={32} />
        </div>
        <div className="absolute top-2/3 left-1/4 text-indigo-200 opacity-20">
          <ArrowRight size={28} />
        </div>
        
        {/* Floating particles */}
        <div className="absolute top-1/4 right-1/3 w-2 h-2 bg-blue-300 rounded-full opacity-40 animate-ping"></div>
        <div className="absolute top-3/4 left-1/5 w-3 h-3 bg-indigo-400 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 right-1/5 w-1 h-1 bg-yellow-400 rounded-full opacity-50 animate-bounce"></div>
      </div>

      <Header />
      <Headline />
      <SectionDivider />
      <Overview />
      <KeyFeatures />
      <WaitlistSection 
        email={email} 
        setEmail={setEmail} 
        handleSubmit={(e) => handleSubmit(e, 'homepage_waitlist')}
        isSubmitting={isSubmitting}
      />
      <HowItWorks />
      <Footer 
        email={email} 
        setEmail={setEmail} 
        handleSubmit={(e) => handleSubmit(e, 'homepage_footer')}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Index;
